import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class OC80CartPicks extends Component {
  static observedAttributes = ['data-product-id', 'data-cart-signature'];
  #controller;
  #drawer;
  #loaded = '';
  #queued = false;

  connectedCallback() {
    super.connectedCallback();
    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener('theme-drawer:open', this.#schedule);
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#restoreFocus);
    this.#schedule();
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.querySelector('[data-oc80-pick]')) this.#loaded = '';
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#loaded = '';
    this.#drawer?.removeEventListener('theme-drawer:open', this.#schedule);
    this.removeEventListener(StandardEvents.cartLinesUpdate, this.#restoreFocus);
  }

  attributeChangedCallback() {
    this.#schedule();
  }

  #restoreFocus = (event) => {
    const drawer = this.#drawer;
    const button = event.target instanceof HTMLElement ? event.target.querySelector('button[type="submit"]') : null;
    if (button) button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    event.promise?.then(({ detail }) => {
      if (!detail?.didError) {
        requestAnimationFrame(() => drawer?.querySelector('[data-oc80-continue]')?.focus({ preventScroll: true }));
      }
    }).catch(() => {}).finally(() => {
      if (button?.isConnected) button.disabled = false;
      this.removeAttribute('aria-busy');
    });
  };

  #schedule = () => {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      this.#load();
    });
  };

  async #load() {
    if (!this.isConnected || !this.dataset.productId) return;
    if (this.#drawer && !this.#drawer.hasAttribute('open')) return;
    const key = `${this.dataset.productId}:${this.dataset.cartSignature}`;
    if (this.#loaded === key) return;
    this.#loaded = key;
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const timeout = setTimeout(() => controller.abort(), 8000);
    this.hidden = true;
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('section_id', 'oc80-cart-picks');
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', 'related');
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = html.querySelector('[data-oc80-picks-result]');
      if (controller.signal.aborted || !this.isConnected || this.#loaded !== key) return;
      if (result?.querySelector('[data-oc80-pick]')) {
        this.replaceChildren(...Array.from(result.childNodes));
        this.hidden = false;
      }
    } catch {
      if (this.#controller === controller) this.#loaded = '';
    } finally {
      clearTimeout(timeout);
    }
  }
}

if (!customElements.get('oc80-cart-picks')) customElements.define('oc80-cart-picks', OC80CartPicks);
